import { useContext, useMemo, useEffect, useState, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import propTypes from 'prop-types';
import Swal from 'sweetalert2';

import ProfileCard from '../../ProfileCard';

import { ReactComponent as BookOpenIcon } from 'assets/icons/book-open.svg';
import { ReactComponent as LogoutIcon } from 'assets/icons/log-out.svg';
import logoImage from 'assets/images/umptkin.png';
import Logo from 'components/Logo';
import { SIDEBAR_EXAM, SIDEBAR_HELP } from 'constant';
import clsx from 'libs/clsx';
import Typography from 'components/Typography';
import ExamControllerContext from 'context/examController.context';

function MobileGuestSidebarActions({
  activeItem,
  sidebarLinks,
  onLogoutClick,
}) {
  return (
    <div className="md:hidden px-4 pb-4">
      <div className="flex items-stretch gap-3">
        {sidebarLinks.map(({ id, to, icon: Icon, text }) => (
          <Link
            key={id}
            to={to}
            className={clsx(
              'flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium shadow-sm',
              id === activeItem
                ? 'bg-green-100 text-green-600'
                : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            )}
          >
            <Icon className="w-3.5" />
            <Typography as="span" className="font-medium text-xs">
              {text}
            </Typography>
          </Link>
        ))}

        <button
          type="button"
          className="flex-1 flex items-center justify-center gap-1 bg-red-50 px-2 py-1 text-xs font-medium text-red-500 shadow-sm hover:bg-red-100 hover:text-red-600"
          onClick={onLogoutClick}
        >
          <LogoutIcon className="w-3.5" />
          <Typography as="span" className="font-medium text-xs">
            Keluar
          </Typography>
        </button>
      </div>
    </div>
  );
}

function Sidebar({ activeItem }) {
  const history = useHistory();
  const [, dispatch] = useContext(ExamControllerContext);

  const [isSidebarLinksAbsolute, setIsSidebarLinkAbsolute] = useState(false);

  const sidebarLinks = useMemo(
    () => [
      {
        id: SIDEBAR_EXAM,
        to: '/simulasi',
        icon: BookOpenIcon,
        text: 'Ujian',
      },
    ],
    []
  );

  const handleLogoutClick = () => {
    Swal.fire({
      title: 'Keluar dari simulasi?',
      text: 'Progress simulasi saat ini akan dihentikan.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Keluar',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#dc2626',
    }).then((result) => {
      if (!result.isConfirmed) return;
      dispatch.reset();
      history.push('/login');
    });
  };

  // Adjust styling for sidebar.
  const adjustStyle = useCallback(() => {
    const parentHeight = window.screen.height;
    const sidebarChildHeight = document
      .getElementById('sidebar-child')
      .getBoundingClientRect().height;
    const sidebarLinksHeight = document
      .getElementById('sidebar-links')
      .getBoundingClientRect().height;

    if (sidebarChildHeight + sidebarLinksHeight > parentHeight) {
      setIsSidebarLinkAbsolute(false);
    } else {
      setIsSidebarLinkAbsolute(true);
    }
  }, []);

  // Listen window resize event.
  useEffect(() => {
    const handle = () => adjustStyle();
    window.addEventListener('resize', handle);

    return () => window.removeEventListener('resize', handle);
  }, [adjustStyle]);

  // Adjust style on mount.
  useEffect(() => {
    adjustStyle();
  }, [adjustStyle]);

  return (
    <div className="relative mb-6 md:mb-0 md:fixed md:top-0 md:left-0 md:right-auto md:bottom-0 bg-white md:2xl:w-80 md:xl:w-80 md:w-72 shadow-lg overflow-y-auto z-10">
      <div
        id="sidebar-child"
        className="pt-3 pb-1 md:pt-12 md:pb-0 flex flex-col md:block"
      >
        {/* Logo */}
        <div className="px-4 md:px-8">
          <div className="hidden md:block">
            <Logo />
          </div>
          <div className="flex items-center gap-3 md:hidden">
            <img
              src={logoImage}
              alt="Logo"
              className="w-10 h-10 object-contain shrink-0"
            />
            <Typography className="text-xl leading-none font-semibold">
              SIMULASI SSE 2026
            </Typography>
          </div>
        </div>

        <ProfileCard />
      </div>

      <MobileGuestSidebarActions
        activeItem={activeItem}
        sidebarLinks={sidebarLinks}
        onLogoutClick={handleLogoutClick}
      />

      {/* Sidebar links */}
      <div
        id="sidebar-links"
        className={clsx(
          'hidden md:block',
          isSidebarLinksAbsolute && 'md:absolute md:inset-x-0 md:bottom-0',
          'mt-2'
        )}
      >
        {sidebarLinks.map(({ id, to, icon: Icon, text }) => (
          <Link
            key={id}
            to={to}
            className={clsx(
              'block px-6 md:px-8 py-4 font-medium',
              id === activeItem
                ? 'bg-green-100 text-green-600 border-l-8 border-green-600 hover:bg-green-200 hover:text-green-700'
                : 'bg-white text-gray-400 border-l-8 border-transparent hover:bg-gray-100 hover:text-gray-600 hover:border-gray-600'
            )}
          >
            <div className="flex items-center gap-4 text-base">
              <Icon className="w-8" />
              <Typography as="span" className="font-medium">
                {text}
              </Typography>
            </div>
          </Link>
        ))}

        {/* Logout button */}
        <button
          type="button"
          className="w-full flex items-center gap-4 text-base text-red-500 border-l-8 border-transparent px-6 md:px-8 py-5 font-medium hover:bg-red-200 hover:text-red-600 hover:border-red-500"
          onClick={handleLogoutClick}
        >
          <LogoutIcon className="w-8" />
          <Typography as="span" className="font-medium">
            Keluar
          </Typography>
        </button>
      </div>
    </div>
  );
}

Sidebar.propTypes = {
  activeItem: propTypes.oneOf([SIDEBAR_EXAM, SIDEBAR_HELP]).isRequired,
};

MobileGuestSidebarActions.propTypes = {
  activeItem: propTypes.oneOf([SIDEBAR_EXAM, SIDEBAR_HELP]).isRequired,
  sidebarLinks: propTypes.arrayOf(propTypes.object).isRequired,
  onLogoutClick: propTypes.func.isRequired,
};

export function GuestDashboardMobileActions() {
  return null;
}

export default Sidebar;
